import { RotateCcw, X } from "lucide-react";

import { Input } from "@/components/ui";
import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import { valueLook } from "@/lib/attributeReview/valueLook";
import {
  fixForText,
  newValueName,
  type PoolChoice,
  type PoolValue,
} from "@/lib/attributeReview/valueView";
import { cn } from "@/lib/utils";

export const VALUE_GRID =
  "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_4rem] items-center gap-2";

type Props = {
  value: PoolValue;
  fix?: AttributeReviewValueFixInput;
  choices: PoolChoice[];
  listId: string;
  setFix: (fix: AttributeReviewValueFixInput) => void;
  undoFix: (storeValue: string) => void;
};

const iconButton =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-transform duration-100 ease-out active:scale-95 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:bg-slate-800 dark:hover:text-slate-200";

export function ValueMapRow({
  value: v,
  fix,
  choices,
  listId,
  setFix,
  undoFix,
}: Props) {
  const { row, reason } = valueLook(v, fix, choices);
  const muted = row.ignored && "opacity-60";
  const isNew = !row.ignored && !reason && row.chip === "new";
  const status = row.ignored
    ? "Ignored"
    : reason ||
      (isNew
        ? "New value, added to our list"
        : row.chip
          ? "Matches our list"
          : "");
  return (
    <div
      className={cn(
        VALUE_GRID,
        "rounded-lg border-b border-slate-100 px-2.5 py-2 dark:border-slate-800",
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            "truncate font-mono text-[13px] text-slate-800 dark:text-slate-200",
            muted,
          )}
          title={v.store_label}
        >
          {v.store_label}
        </span>
        <span
          className={cn(
            "text-xs",
            row.ignored
              ? "text-slate-500 dark:text-slate-400"
              : reason
                ? "text-amber-700 dark:text-amber-300"
                : isNew
                  ? "text-indigo-600 dark:text-indigo-300"
                  : "text-emerald-700 dark:text-emerald-300",
          )}
        >
          {status}
        </span>
      </div>
      <Input
        list={listId}
        aria-label={`Our value for ${v.store_label}`}
        className={cn(
          "h-auto rounded-md border-slate-300 bg-white px-2 py-[5px] text-[13px] dark:border-slate-700 dark:bg-slate-950",
          muted,
        )}
        value={row.name}
        disabled={!row.editable}
        onChange={(e) => setFix(fixForText(v, e.target.value, choices))}
        onKeyDown={(e) => e.key === "Escape" && e.currentTarget.blur()}
      />
      <span className="flex justify-end">
        {row.ignored ? (
          <button
            type="button"
            aria-label={`Bring back ${v.store_label}`}
            title="Bring back"
            className={iconButton}
            onClick={() =>
              fix
                ? undoFix(v.store_value)
                : setFix(fixForText(v, newValueName(v), choices))
            }
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        ) : (
          <>
            {fix && (
              <button
                type="button"
                aria-label={`Undo change for ${v.store_label}`}
                title="Undo change"
                className={iconButton}
                onClick={() => undoFix(v.store_value)}
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              aria-label={`Ignore ${v.store_label}`}
              title="Ignore"
              className={iconButton}
              onClick={() =>
                setFix({ storeValue: v.store_value, ignored: true })
              }
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </span>
    </div>
  );
}
